import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchDetailedInsightsView, fetchFilterOptions } from '@/services/api';

export const useDetailedInsightsData = (filters, metric = 'impressions') => {
  // States for each level view
  const [pocLevel, setPocLevel] = useState(null);
  const [brandLevel, setBrandLevel] = useState(null);
  const [regionLevel, setRegionLevel] = useState(null);
  const [campaignLevel, setCampaignLevel] = useState(null);

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filter options
  const [filterOptions, setFilterOptions] = useState({});

  // Create stable string representation of poc_id array to prevent infinite loops
  const pocIdKey = useMemo(() => {
    const pocId = Array.isArray(filters.poc_id) ? filters.poc_id : (filters.poc_id ? [filters.poc_id] : []);
    return JSON.stringify(pocId.sort());
  }, [filters.poc_id]);

  // Create stable string representation of campaign array to prevent infinite loops
  const campaignKey = useMemo(() => {
    const campaign = Array.isArray(filters.campaign) ? filters.campaign : (filters.campaign ? [filters.campaign] : []);
    return JSON.stringify(campaign.sort());
  }, [filters.campaign]);

  // Create stable string representation of poc_name array to prevent infinite loops
  const pocNameKey = useMemo(() => {
    const pocName = Array.isArray(filters.poc_name) ? filters.poc_name : (filters.poc_name ? [filters.poc_name] : []);
    return JSON.stringify(pocName.sort());
  }, [filters.poc_name]);

  // Track previous filter values to prevent unnecessary re-fetches
  const prevFiltersRef = useRef({});

  // Memoized fetch function - use stable dependencies to prevent infinite loops
  const getDetailedInsights = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const pocIdArray = Array.isArray(filters.poc_id) ? filters.poc_id : (filters.poc_id ? [filters.poc_id] : []);
      const campaignArray = Array.isArray(filters.campaign) ? filters.campaign : (filters.campaign ? [filters.campaign] : []);
      const pocNameArray = Array.isArray(filters.poc_name) ? filters.poc_name : (filters.poc_name ? [filters.poc_name] : []);
      const data = await fetchDetailedInsightsView({
        metric: metric,
        brand: filters.brand,
        region: filters.region,
        campaign_name: campaignArray.length > 0 ? campaignArray : undefined,
        start_date: filters.start_date,
        end_date: filters.end_date,
        poc_id: pocIdArray.length > 0 ? pocIdArray : undefined,
        poc_name: pocNameArray.length > 0 ? pocNameArray : undefined,
        key_account: filters.key_account
      });
      setPocLevel(data.poc_level_view);
      setBrandLevel(data.brand_level_view);
      setRegionLevel(data.region_level_view);
      setCampaignLevel(data.campaign_level_view);
      return data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [filters.brand, filters.region, filters.campaign, filters.start_date, filters.end_date, pocIdKey, filters.key_account, metric]);

  // Track previous values to prevent unnecessary re-fetches
  const prevDataRef = useRef({ filterKey: null, metric: null });

  // Fetch on mount and when filters/metric change
  useEffect(() => {
    const currentFilterKey = JSON.stringify({
      brand: filters.brand,
      region: filters.region,
      campaign: campaignKey,
      start_date: filters.start_date,
      end_date: filters.end_date,
      poc_id: pocIdKey,
      poc_name: pocNameKey,
      key_account: filters.key_account
    });

    // Only fetch if filters or metric actually changed
    if (prevDataRef.current.filterKey === currentFilterKey && prevDataRef.current.metric === metric) {
      return;
    }

    prevDataRef.current = { filterKey: currentFilterKey, metric };
    getDetailedInsights();
  }, [filters.brand, filters.region, campaignKey, filters.start_date, filters.end_date, pocIdKey, pocNameKey, filters.key_account, metric, getDetailedInsights]);

  // Fetch filter options - use stable dependencies to prevent infinite loops
  useEffect(() => {
    // Create a key to compare filter values
    const filterKey = JSON.stringify({
      region: filters.region,
      brand: filters.brand,
      campaign: campaignKey,
      start_date: filters.start_date,
      end_date: filters.end_date,
      poc_id: pocIdKey,
      poc_name: pocNameKey,
      key_account: filters.key_account
    });

    // Only fetch if filters actually changed (or on initial mount)
    if (prevFiltersRef.current.filterKey === filterKey && prevFiltersRef.current.filterKey !== undefined) {
      return;
    }

    prevFiltersRef.current.filterKey = filterKey;

    const fetchFilters = async () => {
      try {
        const pocIdArray = Array.isArray(filters.poc_id) ? filters.poc_id : (filters.poc_id ? [filters.poc_id] : []);
        const campaignArray = Array.isArray(filters.campaign) ? filters.campaign : (filters.campaign ? [filters.campaign] : []);
        const pocNameArray = Array.isArray(filters.poc_name) ? filters.poc_name : (filters.poc_name ? [filters.poc_name] : []);
        const options = await fetchFilterOptions(
          filters.region,
          filters.brand,
          campaignArray.length > 0 ? campaignArray : undefined,
          filters.start_date,
          filters.end_date,
          pocIdArray.length > 0 ? pocIdArray : undefined,
          pocNameArray.length > 0 ? pocNameArray : undefined,
          filters.key_account
        );
        setFilterOptions(options || {});
      } catch (err) {
        setError(err);
      }
    };
    fetchFilters();
  }, [filters.region, filters.brand, campaignKey, filters.start_date, filters.end_date, pocIdKey, pocNameKey, filters.key_account]);

  return {
    pocLevel,
    brandLevel,
    regionLevel,
    campaignLevel,
    isLoading,
    error,
    getDetailedInsights,
    filterOptions,
  };
}; 